// Chain Analysis tab.
//
// Renders the "Full Prerequisite Chain" view from `_local/DA.pdf` page 1. Two
// modes share one render path:
//   - "All clusters", the default: aggregates wrong base questions across every
//     latest-per-topic session_topic in the year level.
//   - one cluster: the chains for every topic in it. The scope filter is on
//     CLUSTERS; the Topic control inside the table filters the rows already
//     loaded and is a different thing.
//
// The Phase 3 chain runtime that would generate real PREREQ_L* attempts is not
// built yet, so:
//   - chain rows come from wrong BASE questions only
//   - theoretical chain depth is derived from the base year level number
//   - expanded prereq rows show "Awaiting Phase 3 chain runtime"
//   - heat bars show real BASE counts, with theoretical PREREQ rows all "not
//     reached" until chain attempts exist
//
// When the runtime ships, the prereq nodes replace the placeholders without
// changing this component's shape.

import React from "react"
import { getStudentTopicAttemptDetail } from "../services/bmd-report.service"
import type {
  BmdReviewQuestion,
  GetTopicAttemptDetailResponse,
} from "../types/bmd-report.types"
import {
  abbreviateYearLevel,
  humanMisconception,
  parseYearNumber,
  plainText,
} from "../utils/formatters"
import { BmdPrereqGraph } from "./BmdPrereqGraph"

const ALL_CLUSTERS = "__all__"
/** Bucket for a topic with no strand mapping, so it is still reachable. */
const UNASSIGNED_CLUSTER = "__unassigned__"

// How much of the branching to show. This view walks one prerequisite per
// level (`prereq_order = 1`), but the test follows every prerequisite of a
// wrong answer (PREREQ_FOLLOW_MODE = "ALL"), so the page shows a single line
// where the student actually gets a tree.
//
//   "NONE"   draw the main line only, and say nothing about the rest.
//   "COUNT"  main line plus "+N more", so the tutor can see there is more
//            without the page exploding.
//   "TREE"   draw every branch. NOT BUILT: the expanded row renders a flat
//            list of steps, so this needs a different component and probably
//            a "show more" cap. Chains reach 13 levels, and fan-out reaches
//            24 prerequisites for a single wrong answer.
//
// Switching between NONE and COUNT is this constant alone. TREE is real work;
// reuse advancePrereqChains rather than writing a third chain walk.
type ChainBranchDisplay = "NONE" | "COUNT" | "TREE"
// `as` rather than a plain annotation. TypeScript narrows a const to its
// literal, which makes the other two branches below unreachable and fails the
// build the moment someone flips this. The whole point is that it gets
// flipped.
const CHAIN_BRANCH_DISPLAY = "NONE" as ChainBranchDisplay

// Stop the drawn chain at the first prerequisite the student answered
// correctly.
//
// This mirrors what the test does: a correct answer ends that chain, and
// nothing below it is ever asked. Drawing the deeper skills implied the
// student would be taken through them. The correct step itself stays, since it
// is the reason the chain ended.
//
// A step with no result does NOT stop anything: the test would serve it.
//
// This only changes what the report draws. Assignment is untouched, and the
// runtime still walks the chain from prereqChain.js.
const CHAIN_STOP_AT_CORRECT = true

// ============================================================================
// Helpers
// ============================================================================

function misconceptionPillStyle(m: string | null | undefined): {
  bg: string
  text: string
} {
  switch (m) {
    case "FUNDAMENTAL":
      return { bg: "#fde2e1", text: "#b42318" }
    case "RECENTLY_LEARNED":
      return { bg: "#dbeafe", text: "#1d4ed8" }
    case "LACK_OF_DEPTH":
      return { bg: "#fef3c7", text: "#92400e" }
    default:
      return { bg: "#e5e7eb", text: "#374151" }
  }
}

// Human-readable misconception name for inline prose (the pills elsewhere use
// their own casing).
function misconceptionLabel(m: string | null | undefined): string {
  switch (m) {
    case "FUNDAMENTAL":
      return "fundamental"
    case "RECENTLY_LEARNED":
      return "recently learned"
    case "LACK_OF_DEPTH":
      return "lack of depth"
    default:
      return "untagged"
  }
}

type PrereqChain = BmdReviewQuestion["theoretical_prereq_chain"]

/** Drops everything below a step the student already answered correctly. */
function stopAtCorrect(nodes: PrereqChain): PrereqChain {
  return (nodes || []).map((n) => ({
    ...n,
    children: n.student_result === "correct" ? [] : stopAtCorrect(n.children),
  }))
}

/** Keeps children[0] at every level, discarding the sibling branches. */
function mainLineOnly(nodes: PrereqChain): PrereqChain {
  if (!nodes || nodes.length === 0) return []
  return [{ ...nodes[0], children: mainLineOnly(nodes[0].children) }]
}

/**
 * The chain as the report draws it. Depth and the node rollup are both derived
 * from this, so the numbers always describe what is on screen.
 */
function chainForDisplay(nodes: PrereqChain): PrereqChain {
  const truncated = CHAIN_STOP_AT_CORRECT ? stopAtCorrect(nodes) : nodes || []
  return CHAIN_BRANCH_DISPLAY === "NONE" ? mainLineOnly(truncated) : truncated
}

/** Length of the main line: children[0] followed down. */
function mainLineLength(
  nodes: BmdReviewQuestion["theoretical_prereq_chain"],
): number {
  return nodes && nodes.length > 0 ? 1 + mainLineLength(nodes[0].children) : 0
}

/** Every node in the tree, including branches the tutor has not opened. */
function countChainNodes(
  nodes: BmdReviewQuestion["theoretical_prereq_chain"],
): number {
  return (nodes || []).reduce((a, n) => a + 1 + countChainNodes(n.children), 0)
}

function chainDepthLabel(depth: number): string {
  if (depth <= 0) return "Root"
  const parts = ["Root"]
  for (let i = 1; i <= depth; i++) parts.push(`L${i}`)
  return parts.join(" → ")
}

// ============================================================================
// Types
// ============================================================================

export type ChainTopicOption = {
  pk_session_topic_key: string
  topic_name: string
  /**
   * The cluster this topic sits in. The filter groups by this; topics with no
   * strand mapping fall into an "Unassigned" bucket rather than disappearing.
   */
  strand_id?: number | null
  strand_name?: string
  // Original year level for label rendering. Falls back to the report's base
  // year level when missing.
  year_level_name?: string | null
}

type ChainRow = {
  question: BmdReviewQuestion
  skillName: string
  subTopicName: string | null
  topicName: string
  yearLevelLabel: string
  yearLevelNumber: number | null
  misconceptionType: string | null
  questionText: string
  // Real (theoretical) prereq chain from prerequisite_skills, up to 3 deep.
  prereqChain: BmdReviewQuestion["theoretical_prereq_chain"]
  // Length of the main line, children[0] followed repeatedly. That is what the
  // "Chain Depth" column has always meant, and it is kept separate from
  // chainNodes so the label does not change meaning now the chain is a tree.
  chainDepth: number
  // Every node in the tree, for the "N nodes total" rollup.
  chainNodes: number
  questionOrder: number
}

// ============================================================================
// Sub-components
// ============================================================================

type SummaryStripProps = {
  studentName: string
  baseYearLabel: string
  scopeLabel: string
  wrongBaseCount: number
  chainsTriggered: number
}

const SummaryStrip: React.FC<SummaryStripProps> = ({
  studentName,
  baseYearLabel,
  scopeLabel,
  wrongBaseCount,
  chainsTriggered,
}) => {
  const initials = studentName
    .split(/\s+/)
    .map((p) => p[0] || "")
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
  return (
    <div className="bg-white rounded shadow-sm p-4 mb-3 d-flex align-items-center flex-wrap">
      <div
        className="d-flex align-items-center justify-content-center rounded-circle me-3 fw-bold"
        style={{
          width: 44,
          height: 44,
          backgroundColor: "#fde2e1",
          color: "#b42318",
        }}
      >
        {initials || "—"}
      </div>
      <div className="flex-grow-1">
        <div className="fw-bold font-size-15 mb-1">{studentName}</div>
        <div className="text-muted font-size-13">
          {baseYearLabel} (Base) · {scopeLabel} · {wrongBaseCount} wrong base
          question{wrongBaseCount === 1 ? "" : "s"} · Chains triggered
        </div>
      </div>
      <span
        className="badge px-3 py-2 font-size-13"
        style={{ backgroundColor: "#fde2e1", color: "#b42318" }}
      >
        Chains: {chainsTriggered}
      </span>
    </div>
  )
}

type ChainTableProps = {
  rows: ChainRow[]
  showTopicColumn: boolean
}

type PageSize = 10 | 25 | 50 | "all"
const PAGE_SIZE_OPTIONS: PageSize[] = [10, 25, 50, "all"]

const CHAIN_FILTER_ALL = "__all__"

const ChainTable: React.FC<ChainTableProps> = ({ rows, showTopicColumn }) => {
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(
    () => new Set(),
  )
  const [pageSize, setPageSize] = React.useState<PageSize>(10)
  const [page, setPage] = React.useState<number>(1)
  // Topic, sub-topic and misconception filters. These narrow what is already
  // loaded; the topic dropdown above the summary strip decides what gets
  // fetched. In single-topic mode the topic filter here is redundant, so it
  // is hidden.
  const [topicFilter, setTopicFilter] = React.useState<string>(CHAIN_FILTER_ALL)
  const [subTopicFilter, setSubTopicFilter] =
    React.useState<string>(CHAIN_FILTER_ALL)
  const [misconceptionFilter, setMisconceptionFilter] =
    React.useState<string>(CHAIN_FILTER_ALL)

  const topicOptions = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.topicName).filter(Boolean))).sort(),
    [rows],
  )
  // Sub-topics follow the chosen topic, so the list can't offer combinations
  // that would return nothing.
  const subTopicOptions = React.useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .filter(
              (r) =>
                topicFilter === CHAIN_FILTER_ALL || r.topicName === topicFilter,
            )
            .map((r) => r.subTopicName)
            .filter((v): v is string => !!v),
        ),
      ).sort(),
    [rows, topicFilter],
  )
  const misconceptionOptions = React.useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.misconceptionType).filter((v): v is string => !!v)),
      ).sort(),
    [rows],
  )

  // A sub-topic chosen under one topic usually doesn't exist under another.
  React.useEffect(() => {
    if (
      subTopicFilter !== CHAIN_FILTER_ALL &&
      !subTopicOptions.includes(subTopicFilter)
    ) {
      setSubTopicFilter(CHAIN_FILTER_ALL)
    }
  }, [subTopicOptions, subTopicFilter])

  const visibleRows = React.useMemo(
    () =>
      rows.filter(
        (r) =>
          (topicFilter === CHAIN_FILTER_ALL || r.topicName === topicFilter) &&
          (subTopicFilter === CHAIN_FILTER_ALL ||
            r.subTopicName === subTopicFilter) &&
          (misconceptionFilter === CHAIN_FILTER_ALL ||
            r.misconceptionType === misconceptionFilter),
      ),
    [rows, topicFilter, subTopicFilter, misconceptionFilter],
  )

  // Reset to page 1 if the result set shrinks below the current page or the
  // page size changes.
  React.useEffect(() => {
    setPage(1)
  }, [pageSize, visibleRows.length])

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded shadow-sm p-4 mb-3">
        <div className="text-uppercase text-muted font-size-12 mb-3">
          Full Prerequisite Chain
        </div>
        <p className="text-muted mb-0">
          Nothing to show here. The student answered every question correctly.
        </p>
      </div>
    )
  }

  const totalNodes = visibleRows.reduce((acc, r) => acc + 1 + r.chainNodes, 0)
  const effectivePageSize =
    pageSize === "all" ? visibleRows.length || 1 : (pageSize as number)
  const totalPages = Math.max(1, Math.ceil(visibleRows.length / effectivePageSize))
  const safePage = Math.min(Math.max(1, page), totalPages)
  const startIdx = (safePage - 1) * effectivePageSize
  const endIdx = Math.min(visibleRows.length, startIdx + effectivePageSize)
  const pageRows = visibleRows.slice(startIdx, endIdx)

  return (
    <div className="bg-white rounded shadow-sm p-4 mb-3">
      <div className="text-uppercase text-muted fw-bold font-size-12 mb-3">
        Full Prerequisite Chain
      </div>
      <div className="d-flex justify-content-between align-items-center mb-2 px-1 flex-wrap gap-2">
        <div className="fw-semibold font-size-14">
          Questions the student got wrong. Click a row to see what it builds on.
        </div>
        <div className="text-muted font-size-12">
          {visibleRows.length} chain{visibleRows.length === 1 ? "" : "s"} ·{" "}
          {totalNodes} nodes total
        </div>
      </div>
      <div className="d-flex align-items-end flex-wrap gap-3 mb-3 px-1">
        {showTopicColumn ? (
          <div>
            <label
              htmlFor="bmd-chain-topic-filter"
              className="form-label font-size-12 text-muted mb-1"
            >
              Topic
            </label>
            <select
              id="bmd-chain-topic-filter"
              className="form-select form-select-sm"
              style={{ minWidth: 180 }}
              value={topicFilter}
              onChange={(e) => setTopicFilter(e.target.value)}
            >
              <option value={CHAIN_FILTER_ALL}>All topics</option>
              {topicOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div>
          <label
            htmlFor="bmd-chain-subtopic-filter"
            className="form-label font-size-12 text-muted mb-1"
          >
            Sub-topic
          </label>
          <select
            id="bmd-chain-subtopic-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 180 }}
            value={subTopicFilter}
            onChange={(e) => setSubTopicFilter(e.target.value)}
            disabled={subTopicOptions.length === 0}
          >
            <option value={CHAIN_FILTER_ALL}>All sub-topics</option>
            {subTopicOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="bmd-chain-misconception-filter"
            className="form-label font-size-12 text-muted mb-1"
          >
            Misconception
          </label>
          <select
            id="bmd-chain-misconception-filter"
            className="form-select form-select-sm"
            style={{ minWidth: 180 }}
            value={misconceptionFilter}
            onChange={(e) => setMisconceptionFilter(e.target.value)}
            disabled={misconceptionOptions.length === 0}
          >
            <option value={CHAIN_FILTER_ALL}>All types</option>
            {misconceptionOptions.map((m) => (
              <option key={m} value={m}>
                {humanMisconception(m)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3 px-1 flex-wrap gap-2">
        <div className="d-flex align-items-center font-size-13 text-muted">
          <label htmlFor="bmd-chain-page-size" className="me-2 mb-0">
            Show
          </label>
          <select
            id="bmd-chain-page-size"
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={String(pageSize)}
            onChange={(e) => {
              const v = e.target.value
              setPageSize(v === "all" ? "all" : (Number(v) as PageSize))
            }}
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={String(opt)} value={String(opt)}>
                {opt === "all" ? "All" : opt}
              </option>
            ))}
          </select>
          <span className="ms-2">per page</span>
        </div>
        <div className="text-muted font-size-12">
          {rows.length === 0
            ? "0 of 0"
            : `${startIdx + 1}–${endIdx} of ${rows.length}`}
        </div>
      </div>
      <div className="table-responsive">
        <table className="table align-middle mb-0">
          <thead>
            <tr className="text-uppercase text-muted fw-bold font-size-11">
              <th style={{ width: 40 }}></th>
              <th style={{ width: "8%" }}>Year</th>
              <th style={{ width: showTopicColumn ? "22%" : "30%" }}>Skill</th>
              <th style={{ width: showTopicColumn ? "22%" : "22%" }}>
                Topic / Subtopic
              </th>
              <th style={{ width: "14%" }}>Misconception</th>
              <th style={{ width: showTopicColumn ? "20%" : "26%" }}>
                Chain Depth
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const key = row.question.pk_session_question_key
              const isExpanded = expandedKeys.has(key)
              const m = misconceptionPillStyle(row.misconceptionType)
              return (
                <React.Fragment key={key}>
                  <tr
                    onClick={() => toggleExpanded(key)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <i
                        className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-muted font-size-11`}
                        aria-hidden="true"
                      ></i>
                    </td>
                    <td>
                      <span
                        className="badge fw-semibold"
                        style={{
                          backgroundColor: "#1f2a44",
                          color: "white",
                        }}
                      >
                        {row.yearLevelLabel}
                      </span>
                    </td>
                    <td className="fw-semibold">{row.skillName}</td>
                    <td className="font-size-13">
                      <div>{row.topicName}</div>
                      {row.subTopicName && (
                        <div className="text-muted font-size-12">
                          {row.subTopicName}
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge px-2 py-1 font-size-11"
                        style={{ backgroundColor: m.bg, color: m.text }}
                      >
                        {humanMisconception(row.misconceptionType)}
                      </span>
                    </td>
                    <td className="font-size-13 text-muted">
                      {chainDepthLabel(row.chainDepth)}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{ padding: 0 }}>
                        <ExpandedChain row={row} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-end align-items-center mt-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 font-size-13 text-muted">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <span>
            Page {safePage} of {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={safePage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="Next page"
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  )
}

const ExpandedChain: React.FC<{ row: ChainRow }> = ({ row }) => {
  return (
    <div className="px-3 py-3" style={{ backgroundColor: "#f8fafc" }}>
      <div
        className="d-flex align-items-center mb-2 ps-3"
        style={{ borderLeft: "3px solid #dc3545" }}
      >
        <span
          className="badge me-3"
          style={{ backgroundColor: "#1f2a44", color: "white", minWidth: 90 }}
        >
          BASE {row.yearLevelLabel.toUpperCase()}
        </span>
        <i
          className="fas fa-times-circle me-2"
          style={{ color: "#dc3545" }}
        ></i>
        <span className="font-size-13">
          <span className="fw-semibold">Q{row.questionOrder}</span> ·{" "}
          {row.skillName} · {humanMisconception(row.misconceptionType)}
        </span>
      </div>
      {row.prereqChain.length === 0 ? (
        <div className="text-muted font-size-12 ps-3">
          No prerequisites mapped for this skill yet.
        </div>
      ) : (
        <ChainBranch nodes={row.prereqChain} parentLabel={row.skillName} />
      )}
    </div>
  )
}

// One prerequisite step. Split out so the main line and an expanded sibling
// branch render identically: a branch is not a lesser thing than the line.
const ChainStepRow: React.FC<{
  step: BmdReviewQuestion["theoretical_prereq_chain"][number]
  siblingCount: number
  expanded: boolean
  onToggle: () => void
}> = ({ step, siblingCount, expanded, onToggle }) => (
  <div
    className="d-flex align-items-center mb-2 ps-3"
    style={{ borderLeft: "3px solid #cbd5e1" }}
  >
    <span
      className="badge me-3"
      style={{ backgroundColor: "#e2e8f0", color: "#475569", minWidth: 90 }}
    >
      PRE-REQ L{step.level}
    </span>
    <i
      className={
        step.student_result === "correct"
          ? "fas fa-check-circle me-2"
          : step.student_result === "wrong"
            ? "fas fa-times-circle me-2"
            : "fas fa-circle-notch me-2"
      }
      style={{
        color:
          step.student_result === "correct"
            ? "#166B45"
            : step.student_result === "wrong"
              ? "#A8271C"
              : "#94a3b8",
      }}
      aria-hidden="true"
    ></i>
    <span className="font-size-13">
      {step.year_level_name
        ? `${abbreviateYearLevel(step.year_level_name)} · `
        : ""}
      {plainText(step.skill_name) || "Skill (unmapped)"}{" "}
      {/* Only claim "not yet tested" when that is true. The student has
          usually answered these already, because a prerequisite of one
          question is often another question in the same test, and showing the
          real result saves re-testing. */}
      {step.student_result === "correct" ? (
        <span style={{ color: "#166B45" }}>(answered correctly)</span>
      ) : step.student_result === "wrong" ? (
        <span style={{ color: "#A8271C" }}>
          (answered wrong
          {step.student_misconception
            ? ` · ${misconceptionLabel(step.student_misconception)}`
            : ""}
          )
        </span>
      ) : (
        <span className="text-muted">(not yet tested)</span>
      )}
      {step.repeat ? (
        <span
          className="text-muted ms-1 font-size-12"
          title="Already shown earlier in this chain. The student is only asked about a skill once."
        >
          (also above)
        </span>
      ) : null}
      {siblingCount > 0 && CHAIN_BRANCH_DISPLAY !== "NONE" ? (
        CHAIN_BRANCH_DISPLAY === "TREE" ? (
          <button
            type="button"
            className="btn btn-link p-0 ms-2 font-size-12 align-baseline"
            onClick={onToggle}
            aria-expanded={expanded}
          >
            {expanded
              ? "Hide"
              : `+${siblingCount} more prerequisite${
                  siblingCount === 1 ? "" : "s"
                }`}
          </button>
        ) : (
          <span className="text-muted ms-1">
            (+{siblingCount} more prerequisite{siblingCount === 1 ? "" : "s"})
          </span>
        )
      ) : null}
    </span>
  </div>
)

// Draws one level of the tree: the main line (children[0]) continues straight
// down, and its siblings sit behind a toggle. Opening a sibling renders that
// whole branch the same way, so the page reads as a single line until the
// tutor asks for more.
const ChainBranch: React.FC<{
  nodes: BmdReviewQuestion["theoretical_prereq_chain"]
  // The skill these nodes are prerequisites of. Needed because siblings belong
  // to the parent, not to nodes[0]; labelling them against nodes[0] claimed
  // they were a level deeper than they are.
  parentLabel: string
}> = ({ nodes, parentLabel }) => {
  const [expanded, setExpanded] = React.useState(false)
  if (!nodes || nodes.length === 0) return null
  const [main, ...siblings] = nodes
  return (
    <>
      <ChainStepRow
        step={main}
        siblingCount={siblings.length}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
      />
      {/* Branches render directly under the step they come off, before the
          main line continues. Rendering them after the recursion put a branch
          off L1 below L7, where it read as a prerequisite of L7, the opposite
          of what it means. The main line stays scannable because it is the
          only thing at this indent level; branches are inset. */}
      {expanded ? (
        <div
          className="ms-4 my-1"
          style={{ borderLeft: "2px dashed #cbd5e1" }}
        >
          <div
            className="text-muted ps-3 mb-1"
            style={{ fontSize: "11px" }}
          >
            {parentLabel || "This skill"} also builds on:
          </div>
          {/* Siblings share `main`'s parent, so they keep the same parentLabel
              and the same level. They are alternatives at this level, not a
              level deeper. */}
          {siblings.map((sib) => (
            <ChainBranch
              key={sib.skill_id}
              nodes={[sib]}
              parentLabel={parentLabel}
            />
          ))}
        </div>
      ) : null}
      <ChainBranch
        nodes={main.children}
        parentLabel={plainText(main.skill_name) || "This skill"}
      />
    </>
  )
}

// ============================================================================
// Main component
// ============================================================================

export type BmdChainAnalysisTabProps = {
  // Distinct topics for this student and year level. Drives the topic
  // dropdown; pass an empty list when the student has no BMD data yet.
  topicOptions: ChainTopicOption[]
  // When provided, the tab opens with this topic preselected, which is what
  // the "Chain" button on a Main Report row does. Null defaults to the "all
  // topics" view. It seeds internal state only.
  initialTopicKey: string | null
  studentName: string
  baseYearLevelName: string | null
  // Required for the prerequisite-skill graph fetch.
  studentKey: string
  /** Caps the prerequisite tree to the year the test was set at. */
  baseYearLevelId?: number | null
}

export const BmdChainAnalysisTab: React.FC<BmdChainAnalysisTabProps> = ({
  topicOptions,
  initialTopicKey,
  studentName,
  baseYearLevelName,
  baseYearLevelId = null,
  studentKey,
}) => {
  // Internal selection, where null means "all clusters". The Main Report's
  // "Chain" button still hands over a session TOPIC, so it is resolved to that
  // topic's cluster on mount: the tutor clicked a row and expects to land on
  // it, and the filter is one level up from what they clicked.
  const [selectedClusterKey, setSelectedClusterKey] = React.useState<
    string | null
  >(() => {
    if (!initialTopicKey) return null
    const t = topicOptions.find(
      (o) => o.pk_session_topic_key === initialTopicKey,
    )
    if (!t) return null
    return t.strand_name ? String(t.strand_name) : UNASSIGNED_CLUSTER
  })
  const [results, setResults] = React.useState<
    GetTopicAttemptDetailResponse[]
  >([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)

  // The clusters present, in the order the topics arrive. The filter is on
  // CLUSTERS now, not topics: a tutor thinks "show me the Measurement chains",
  // and a cluster's topics all describe the same area anyway.
  const clusterOptions = React.useMemo(() => {
    const seen = new Map<string, string>()
    for (const t of topicOptions) {
      const key = t.strand_name ? String(t.strand_name) : UNASSIGNED_CLUSTER
      if (!seen.has(key)) {
        seen.set(key, t.strand_name || "Unassigned")
      }
    }
    return Array.from(seen.entries()).map(([key, label]) => ({ key, label }))
  }, [topicOptions])

  const topicsInCluster = React.useCallback(
    (clusterKey: string) =>
      topicOptions.filter(
        (t) =>
          (t.strand_name ? String(t.strand_name) : UNASSIGNED_CLUSTER) ===
          clusterKey,
      ),
    [topicOptions],
  )

  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)

    // One cluster means every topic in it, which the fan-out below already
    // handles - it was written for "All topics" and needs no change here.
    const targetKeys: string[] =
      selectedClusterKey == null
        ? topicOptions.map((t) => t.pk_session_topic_key)
        : topicsInCluster(selectedClusterKey).map((t) => t.pk_session_topic_key)

    if (targetKeys.length === 0) {
      setResults([])
      setLoading(false)
      return () => controller.abort()
    }

    // allSettled, not all. In "All topics" mode this fans out one request per
    // topic, and Promise.all rejects as soon as any one of them fails, so a
    // single bad topic blanked the whole tab with a generic error. A student
    // with seven topics had seven chances to lose the view and a student with
    // one never hit it, which is why it only showed up on a larger test. The
    // topics that loaded now still render, and the failure is named.
    Promise.allSettled(
      targetKeys.map((k) =>
        getStudentTopicAttemptDetail(
          { session_topic_key: k },
          controller.signal,
        ),
      ),
    )
      .then((settled) => {
        if (controller.signal.aborted) return
        const ok: GetTopicAttemptDetailResponse[] = []
        const failedKeys: string[] = []
        settled.forEach((s, i) => {
          if (s.status === "fulfilled") ok.push(s.value)
          else failedKeys.push(targetKeys[i])
        })
        setResults(ok)
        if (failedKeys.length > 0) {
          const names = failedKeys.map(
            (k) =>
              topicOptions.find((t) => t.pk_session_topic_key === k)
                ?.topic_name || k,
          )
          setError(
            ok.length === 0
              ? `Couldn't load the chain analysis (${names.join(", ")}).`
              : `Couldn't load ${names.join(", ")}. The other topics are shown below.`,
          )
        }
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load chain analysis")
        setLoading(false)
      })
    return () => controller.abort()
  }, [selectedClusterKey, topicOptions, topicsInCluster])

  const baseYearLabel = abbreviateYearLevel(baseYearLevelName)

  // Build chain rows from every loaded result. Each result is one
  // session_topic, and its wrong BASE questions become rows.
  const rows: ChainRow[] = []
  let totalWrongBase = 0
  for (const data of results) {
    const yearLabel = abbreviateYearLevel(
      data.topic.year_level_name || baseYearLevelName,
    )
    const yearNumber = parseYearNumber(
      data.topic.year_level_name || baseYearLevelName,
    )
    const baseQs = data.questions.filter((q) => q.phase === "BASE")
    for (const q of baseQs) {
      const isWrong = q.attempt && q.attempt.is_correct === false
      if (isWrong) totalWrongBase += 1
      if (!isWrong) continue
      const skillName =
        plainText(q.skill_name) ||
        plainText(q.snapshot?.diagnostic_text).slice(0, 80) ||
        "Skill (unmapped)"
      const prereqChain = chainForDisplay(q.theoretical_prereq_chain || [])
      rows.push({
        question: q,
        skillName,
        subTopicName: plainText(q.sub_topic_name),
        topicName: plainText(data.topic.topic_name),
        yearLevelLabel: yearLabel,
        yearLevelNumber: yearNumber,
        misconceptionType: q.attempt?.misconception_type ?? null,
        questionText: plainText(q.snapshot?.diagnostic_text),
        prereqChain,
        chainDepth: mainLineLength(prereqChain),
        chainNodes: countChainNodes(prereqChain),
        questionOrder: q.question_order || 0,
      })
    }
  }

  const isAllMode = selectedClusterKey == null
  const scopeLabel = isAllMode
    ? "All clusters"
    : `Cluster: ${
        clusterOptions.find((c) => c.key === selectedClusterKey)?.label || "—"
      }`
  const chainsTriggered = rows.length

  return (
    <>
      {/* Cluster filter row, above the summary strip. */}
      <div className="bg-white rounded shadow-sm p-3 mb-3 d-flex align-items-center flex-wrap gap-2">
        <label
          htmlFor="bmd-chain-cluster-filter"
          className="text-uppercase text-muted font-size-12 fw-semibold mb-0 me-2"
        >
          Cluster:
        </label>
        <select
          id="bmd-chain-cluster-filter"
          className="form-select form-select-sm"
          style={{ minWidth: 220, maxWidth: 320 }}
          value={selectedClusterKey ?? ALL_CLUSTERS}
          onChange={(e) => {
            const v = e.target.value
            setSelectedClusterKey(v === ALL_CLUSTERS ? null : v)
          }}
          disabled={clusterOptions.length === 0}
        >
          <option value={ALL_CLUSTERS}>All clusters</option>
          {clusterOptions.map((c) => (
            <option key={c.key} value={c.key}>
              {plainText(c.label)}
            </option>
          ))}
        </select>
        <span className="text-muted font-size-12 ms-auto">
          {clusterOptions.length === 0
            ? "No clusters yet."
            : isAllMode
              ? `${clusterOptions.length} cluster${clusterOptions.length === 1 ? "" : "s"} aggregated`
              : `${topicsInCluster(selectedClusterKey).length} topic${
                  topicsInCluster(selectedClusterKey).length === 1 ? "" : "s"
                } in this cluster`}
        </span>
      </div>

      {topicOptions.length === 0 ? (
        <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
          No clusters yet for this student at this year level.
        </div>
      ) : loading ? (
        <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
          Loading chain analysis…
        </div>
      ) : error ? (
        <div className="bg-white rounded shadow-sm p-5 text-center text-danger">
          {error}
        </div>
      ) : (
        <>
          <SummaryStrip
            studentName={studentName}
            baseYearLabel={baseYearLabel}
            scopeLabel={scopeLabel}
            wrongBaseCount={totalWrongBase}
            chainsTriggered={chainsTriggered}
          />
          <BmdPrereqGraph
            studentKey={studentKey}
            baseYearLevelId={baseYearLevelId}
          />
          <ChainTable rows={rows} showTopicColumn={isAllMode} />
        </>
      )}
    </>
  )
}

export default BmdChainAnalysisTab
